export interface SharedApiRequestOptions {
  method: string;
  body?: unknown;
  /** Extra request headers (e.g. the intake flow's X-Intake-Resume
   *  capability). JSON Content-Type is still added when a body is set. */
  headers?: Record<string, string>;
}

function apiBase(): string {
  // Read lazily so tests can change the public base per request. Next also
  // statically inlines NEXT_PUBLIC_* expressions during client builds.
  return process.env.NEXT_PUBLIC_API_BASE_URL ?? "";
}

// ── Expired-access-token recovery (#643) ─────────────────────────────────
// The access cookie lives 60 minutes. A call that 401s past that point is
// refreshed ONCE and replayed once. This is the reference's "Fix #3: 401
// interceptor" (reference/scopely-frontend/src/lib/api-request.ts), carried
// here because every spine call already goes through this primitive —
// without it a rep who signed in an hour ago saw "Authentication credentials
// were not provided" on save and send (UAT band 2, #640).
//
// ONE refresh authority per origin, with per-tab single-flight. The spine rotates the refresh token on
// every success and revokes the whole token family when a rotated-out token
// is presented again (apps/authentication/authentication.py), so two refresh
// POSTs in flight from one tab — the session provider's 45-minute timer and
// this interceptor waking together after a closed lid — would sign the user
// out. Every request to the refresh endpoint, whoever makes it, therefore
// shares the same in-flight promise.
//
// A 401 from login/ means bad credentials and from refresh/ means the session
// is over; neither is a token to refresh, so neither is replayed. A failed
// refresh hands the ORIGINAL 401 back untouched: the callers' typed-error
// conversion and the session provider's signed-out mapping are unchanged.
const REFRESH_PATH = "/api/spine/v1/auth/refresh/";
const NEVER_REPLAYED = ["/auth/login/", "/auth/refresh/"];

let inflightRefresh: Promise<Response> | null = null;

async function refreshAcrossTabs(init: RequestInit): Promise<Response> {
  // Keep creation of fetch inside the lock: cookies are selected when it sends.
  // Never relax backend reuse detection or store refresh credentials in JS.
  if (typeof navigator === "undefined" || !navigator.locks) {
    throw new Error("Secure cross-tab session refresh is unavailable; sign in again using a supported browser.");
  }
  return navigator.locks.request("vipzw-session-refresh", () => fetch(`${apiBase()}${REFRESH_PATH}`, init));
}

function refreshOnce(init: RequestInit): Promise<Response> {
  if (!inflightRefresh) {
    inflightRefresh = refreshAcrossTabs(init).finally(() => {
      inflightRefresh = null;
    });
  }
  return inflightRefresh;
}

function buildInit({ method, body, headers }: SharedApiRequestOptions): RequestInit {
  // Multipart passes through untouched: the browser sets the boundary
  // Content-Type itself, and JSON-stringifying a FormData would send
  // "{}" (§6.19's CSV upload is the first caller).
  if (body instanceof FormData) {
    return {
      method,
      credentials: "include",
      ...(headers ? { headers } : {}),
      body,
    };
  }
  return {
    method,
    credentials: "include",
    ...(body === undefined
      ? headers
        ? { headers }
        : {}
      : {
          headers: { "Content-Type": "application/json", ...headers },
          body: JSON.stringify(body),
        }),
  };
}

/**
 * Performs the common browser transport work for spine API adapters.
 *
 * This layer deliberately returns the raw Response. Endpoint namespaces,
 * response parsing, no-content semantics, and typed error conversion remain
 * owned by their feature modules.
 */
export async function sharedApiRequest(
  path: string,
  options: SharedApiRequestOptions,
): Promise<Response> {
  const url = `${apiBase()}${path}`;
  const init = buildInit(options);
  if (path === REFRESH_PATH) {
    // Each consumer gets its own body to read; the shared Response stays
    // unread so a concurrent consumer can clone it too.
    return refreshOnce(init).then((res) => res.clone());
  }
  const res = await fetch(url, init);
  if (res.status !== 401 || NEVER_REPLAYED.some((p) => path.includes(p))) {
    return res;
  }
  const refreshed = await refreshOnce(
    buildInit({ method: "POST", body: {} }),
  ).then(
    (r) => r.ok,
    () => false,
  );
  if (!refreshed) return res;
  // Same method, headers and body; the browser attaches the rotated cookie.
  // A 401 on the replay is returned as is — the replay is never itself retried.
  return fetch(url, init);
}
